/**
 * Zod schemas for validating raw Olympics schedule data
 * These schemas validate JSON data before enrichment
 *
 * TypeScript types are inferred from Zod schemas using z.infer
 */

import { z } from 'zod';

const statusTypeSchema = z.string();

const participantSchema = z.object({
  name: z.string().min(1, 'Participant name cannot be empty'),
  countryId: z.number().int().positive('Country ID must be a positive integer'),
});

/**
 * Schema for individual result entry within the results array
 */
const resultEntrySchema = z.object({
  type: z.string(),
  value: z.string(),
});

/**
 * Schema for competition results
 * New structure includes resultType and results array
 *
 * Rank is almost always present, except in team games like ice hockey
 * Medal is present only in medal events (rank may be 1,2, or 3, but it doesn't mean a medal)
 */
const resultsSchema = z.object({
  resultType: z.enum(['no_team', 'team']),
  results: z.array(resultEntrySchema),
  rank: z.string().optional(),
  medal: z.enum(['gold', 'silver', 'bronze']).optional(),
});

const eventParticipantSchema = z.object({
  participant: participantSchema,
  results: resultsSchema.optional(),
});

/**
 * Main schema for Olympics events
 * Validates the structure from merged-data.json
 */
export const olympicsEventSchema = z.object({
  id: z.number().int().positive('Event ID must be a positive integer'),
  name: z.string().min(1, 'Event name cannot be empty'),
  statusType: statusTypeSchema,
  eventParticipants: z.array(eventParticipantSchema),
  date: z.string(),
  hasFin: z.boolean(),
  tv: z.string().optional(),
  insight: z.string().nullable().optional(),
  correction: z.string().optional(),
  urls: z.array(z.string().url()).optional(),
  medal: z.boolean().optional(),
});

export const scheduleDataSchema = z.array(olympicsEventSchema);

// Inferred TypeScript types from Zod schemas
export type StatusType = z.infer<typeof statusTypeSchema>;
export type Participant = z.infer<typeof participantSchema>;
export type Results = z.infer<typeof resultsSchema>;
export type EventParticipant = z.infer<typeof eventParticipantSchema>;
export type OlympicsEvent = z.infer<typeof olympicsEventSchema>;
export type ScheduleData = z.infer<typeof scheduleDataSchema>;

export interface ValidationResult<T> {
  success: boolean;
  data?: T;
  errors?: string[];
}

/**
 * Validates schedule data with detailed error messages
 *
 * @param data - Unknown data to validate
 * @returns Validation result with data or errors
 */
export function validateScheduleData(data: unknown): ValidationResult<any> {
  try {
    const result = scheduleDataSchema.parse(data);
    return { success: true, data: result };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = error.errors.map((err) => {
        const path = err.path.join('.');
        return `${path ? path + ': ' : ''}${err.message}`;
      });
      return { success: false, errors };
    }
    return { success: false, errors: ['Unknown validation error'] };
  }
}
